/**
 * DS-1202 experimental Link recipe: a full parallel of `linkVariants` whose
 * destructive tone consumes the reworked `semantic-*` tier (text role +
 * surface ladder + active-text state). Behind
 * `release-experimental-design-system-forms`. Non-destructive tones are
 * unchanged copies — delete this file when experimental becomes canonical.
 */
import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import { focusRing } from '../styles/focus-ring';
import { INHERIT_TYPE_SIZE_CLASS } from '../styles/inherit-type';
import { typographyVariant } from '../typography';

/**
 * Size classes for the standalone variant (ghost button-like)
 * Each size includes:
 * - gap for icon spacing
 * - padding for hover background
 * - negative margin to offset padding (so link doesn't eat into its space)
 */
const standaloneSizeClasses = {
  inherit: 'mx-[-0.125em] gap-x-[0.25em] px-[0.125em]',
  xs: '-mx-1 gap-x-1 px-1',
  sm: '-mx-1 gap-x-1 px-1',
  md: '-mx-1 gap-x-1 px-1',
  lg: '-mx-1 gap-x-1.5 px-1',
  xl: '-mx-1 gap-x-1.5 px-1',
  xxl: '-mx-1 gap-x-1.5 px-1',
} as const;

/**
 * Size classes for the inline variant (underlined link)
 * Only includes gap for icon spacing - no padding/margin
 */
const inlineSizeClasses = {
  inherit: 'gap-x-[0.25em]',
  xs: 'gap-x-1',
  sm: 'gap-x-1',
  md: 'gap-x-1',
  lg: 'gap-x-1.5',
  xl: 'gap-x-1.5',
  xxl: 'gap-x-1.5',
} as const;

/**
 * Icon size classes for each link size
 * 'inherit' uses 1em to match the parent font size
 */
const iconSizeClasses = {
  inherit: '[&_svg]:size-[1em]',
  xs: '[&_svg]:size-3 [&_svg]:stroke-icon-xs',
  sm: '[&_svg]:size-3.5 [&_svg]:stroke-icon-sm',
  md: '[&_svg]:size-4 [&_svg]:stroke-icon-md',
  lg: '[&_svg]:size-4.5 [&_svg]:stroke-icon-lg',
  xl: '[&_svg]:size-5 [&_svg]:stroke-icon-xl',
  xxl: '[&_svg]:size-6 [&_svg]:stroke-icon-xxl',
} as const;

export const linkVariantsExperimental = cva(
  cn(
    'inline-flex w-fit cursor-pointer items-baseline rounded-sm',
    '[&_svg]:self-center',
    'transition-[color,background-color,text-decoration-color] duration-300',
    focusRing
  ),
  {
    variants: {
      variant: {
        inline:
          'underline decoration-[clamp(1px,0.1em,3px)] underline-offset-[clamp(2px,0.225em,6px)]',
        standalone: '',
      },
      tone: {
        default: '',
        destructive: '',
      },
      size: {
        inherit: cn('font-[inherit]', INHERIT_TYPE_SIZE_CLASS, iconSizeClasses.inherit),
        xs: cn(typographyVariant({ variant: 'captionBold' }), iconSizeClasses.xs),
        sm: cn(typographyVariant({ variant: 'p2Bold' }), iconSizeClasses.sm),
        md: cn(typographyVariant({ variant: 'pBold' }), iconSizeClasses.md),
        lg: cn(typographyVariant({ variant: 'pBold' }), iconSizeClasses.lg),
        xl: cn(typographyVariant({ variant: 'pBold' }), iconSizeClasses.xl),
        xxl: cn(typographyVariant({ variant: 'pBold' }), iconSizeClasses.xxl),
      },
      // Disabled fades uniformly with `opacity-50` over the resting tone
      disabled: {
        true: 'pointer-events-none cursor-not-allowed opacity-50',
        false: '',
      },
    },
    compoundVariants: [
      // Variant + size spacing
      {
        variant: 'standalone',
        size: 'inherit',
        className: standaloneSizeClasses.inherit,
      },
      { variant: 'standalone', size: 'xs', className: standaloneSizeClasses.xs },
      { variant: 'standalone', size: 'sm', className: standaloneSizeClasses.sm },
      { variant: 'standalone', size: 'md', className: standaloneSizeClasses.md },
      { variant: 'standalone', size: 'lg', className: standaloneSizeClasses.lg },
      { variant: 'standalone', size: 'xl', className: standaloneSizeClasses.xl },
      { variant: 'standalone', size: 'xxl', className: standaloneSizeClasses.xxl },
      { variant: 'inline', size: 'inherit', className: inlineSizeClasses.inherit },
      { variant: 'inline', size: 'xs', className: inlineSizeClasses.xs },
      { variant: 'inline', size: 'sm', className: inlineSizeClasses.sm },
      { variant: 'inline', size: 'md', className: inlineSizeClasses.md },
      { variant: 'inline', size: 'lg', className: inlineSizeClasses.lg },
      { variant: 'inline', size: 'xl', className: inlineSizeClasses.xl },
      { variant: 'inline', size: 'xxl', className: inlineSizeClasses.xxl },

      // Tone styles.
      {
        variant: 'inline',
        tone: 'default',
        className: cn(
          'text-text-secondary decoration-text-secondary/50',
          'hover:text-text-secondary hover:decoration-text-secondary',
          'focus-visible:text-text-secondary focus-visible:decoration-text-secondary'
        ),
      },
      {
        variant: 'inline',
        tone: 'destructive',
        className: cn(
          'text-semantic-text-destructive decoration-semantic-text-destructive/50',
          'hover:text-semantic-text-destructive hover:decoration-semantic-text-destructive',
          // On press, text + underline darken to the active text step.
          'active:text-semantic-text-destructive-active active:decoration-semantic-text-destructive-active',
          'focus-visible:text-semantic-text-destructive focus-visible:decoration-semantic-text-destructive'
        ),
      },
      {
        variant: 'standalone',
        tone: 'default',
        className: cn(
          'text-text-secondary',
          'hover:bg-surface-hover hover:text-text-primary',
          'focus-visible:bg-surface-hover focus-visible:text-text-primary'
        ),
      },
      {
        variant: 'standalone',
        tone: 'destructive',
        // Mirrors the `destructive_ghost` button recipe: text tone + surface
        // ladder, with the text darkening to the active step on press.
        className: cn(
          'text-semantic-text-destructive',
          'hover:bg-semantic-background-destructive-hover',
          'focus-visible:bg-semantic-background-destructive-hover',
          'active:bg-semantic-background-destructive-active active:text-semantic-text-destructive-active'
        ),
      },
    ],
    defaultVariants: {
      variant: 'standalone',
      tone: 'default',
      size: 'inherit',
      disabled: false,
    },
  }
);
